import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type { Sermon } from './useSermons';

// Override fields to allow null so we can intentionally clear them.
export type UpsertSermonPayload = Omit<Sermon, 'series' | 'id' | 'notes' | 'thumbnail_url' | 'video_url' | 'video_url_secondary'> & {
  id?: string; // optional for insert
  series_id?: string | null;
  video_url?: string | null;
  video_url_secondary?: string | null;
  thumbnail_url?: string | null;
  status?: string | null;
  notes?: string | null;
  confession_image_urls?: string[] | null;
};

export const useUpsertSermon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpsertSermonPayload) => {
      // Uses anon client; ensure RLS on sermons permits insert/update for the current role
      const { id, ...rest } = payload;
      if (id) {
        const { data, error } = await supabase
          .from('sermons')
          .update(rest)
          .eq('id', id)
          .select()
          .single();
        if (error) throw new Error(error.message);
        return data;
      }
      const { data, error } = await supabase.from('sermons').insert(rest).select().single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sermons', 'v2'] });
      queryClient.invalidateQueries({ queryKey: ['sermon-assets'] });
    },
  });
};
